import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import { extractProjectName } from '@/common/utils'
import type { StartSessionFormState } from '../../types'

interface ReviewStepProps {
  readonly form: StartSessionFormState
  readonly projectKey: string
  readonly isSubmitting: boolean
  readonly error: string | null
  readonly onSubmit: () => void
  readonly onBack: () => void
}

export const ReviewStep: FC<ReviewStepProps> = ({
  form,
  projectKey,
  isSubmitting,
  error,
  onSubmit,
  onBack,
}) => {
  const projectDisplayName = extractProjectName(projectKey)
  const hasDistinctPath = projectDisplayName !== projectKey
  const truncatedMessage =
    form.initialMessage.length > 120
      ? `${form.initialMessage.substring(0, 120)}...`
      : form.initialMessage

  return (
    <Box sx={containerSx}>
      <Typography variant="h6" sx={titleSx}>
        Review New Session
      </Typography>

      <Box sx={summaryListSx}>
        <SummaryRow label="Session Name" value={form.sessionName} />
        {hasDistinctPath ? (
          <SummaryRow label="Project" value={projectDisplayName} secondary={projectKey} />
        ) : (
          <SummaryRow label="Project" value={projectDisplayName} />
        )}
        <SummaryRow label="Initial Message" value={truncatedMessage || 'None'} />
        {form.dangerouslySkipPermissions ? (
          <SummaryRow
            label="Permissions"
            value="Dangerously Skip Permissions"
            valueSx={dangerousValueSx}
          />
        ) : (
          <SummaryRow label="Permissions" value="Standard (safe)" />
        )}
      </Box>

      {form.dangerouslySkipPermissions && (
        <Alert severity="warning" sx={alertSx}>
          This session will execute all tools without permission prompts. Ensure the task is safe
          before proceeding.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={alertSx}>
          {error}
        </Alert>
      )}

      <Box sx={actionsSx}>
        <Button onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Starting...' : 'Start Session'}
        </Button>
      </Box>
    </Box>
  )
}

interface SummaryRowProps {
  readonly label: string
  readonly value: string
  readonly secondary?: string
  readonly valueSx?: SxProps<Theme>
}

const SummaryRow: FC<SummaryRowProps> = ({ label, value, secondary, valueSx }) => (
  <Box sx={summaryRowSx}>
    <Typography variant="body2" color="text.secondary" sx={summaryLabelSx}>
      {label}
    </Typography>
    {valueSx ? (
      <Typography variant="body2" sx={valueSx}>
        {value}
      </Typography>
    ) : (
      <Typography variant="body2">{value}</Typography>
    )}
    {secondary && (
      <Typography variant="caption" color="text.disabled" sx={summarySecondarySx}>
        {secondary}
      </Typography>
    )}
  </Box>
)

const containerSx: SxProps<Theme> = {
  py: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 3,
}

const summaryListSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  mb: 3,
}

const summaryRowSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
}

const summaryLabelSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.75rem',
  letterSpacing: '0.05em',
}

const summarySecondarySx: SxProps<Theme> = {
  mt: 0.25,
  wordBreak: 'break-all',
}

const dangerousValueSx: SxProps<Theme> = {
  color: 'warning.main',
  fontWeight: 600,
}

const alertSx: SxProps<Theme> = {
  mb: 2,
}

const actionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 4,
}
